import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  TextField,
  Alert
} from '@mui/material';

/**
 * DeleteDialog component - A reusable confirmation dialog for deletion operations.
 * Captures an optional reason ("why") that is recorded in the audit log of any
 * surviving linked items.
 *
 * @component
 */
const DeleteDialog = ({ open, onClose, onConfirm, itemTitle, itemType = 'item', childCount = 0 }) => {
  const [reason, setReason] = useState('');

  // Reset the reason each time the dialog opens.
  useEffect(() => {
    if (open) setReason('');
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="delete-dialog-title"
      aria-describedby="delete-dialog-description"
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle id="delete-dialog-title">
        Delete {itemType}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="delete-dialog-description">
          {itemTitle
            ? `Are you sure you want to delete "${itemTitle}"?`
            : `Are you sure you want to delete this ${itemType}?`}
          This action cannot be undone.
        </DialogContentText>
        {childCount > 0 && (
          // #347/#658: archiving/deleting a parent CASCADES to its whole live
          // subtree — the count is now the real (whole-subtree) live-descendant
          // count (`getArchiveImpact`), and the archive really does cascade to
          // all of them, so this warning is no longer aspirational.
          <Alert severity="warning" sx={{ mt: 2 }} data-testid="delete-children-warning">
            This action will also archive all {childCount} child item{childCount === 1 ? '' : 's'}.
          </Alert>
        )}
        <TextField
          margin="dense"
          label="Reason (optional)"
          placeholder="Why is this being deleted? Recorded in the audit log."
          fullWidth
          size="small"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button
          onClick={() => onConfirm(reason)}
          color="error"
          variant="contained"
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

DeleteDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  itemTitle: PropTypes.string,
  itemType: PropTypes.string,
  childCount: PropTypes.number
};

export default DeleteDialog;
